import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { PenLine, Trash2, Plus, X, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import FileUpload from "@/components/FileUpload";
import AttachmentList from "@/components/AttachmentList";
import RichTextEditor from "@/components/RichTextEditor";

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", summary: "", tags: "", status: "draft", attachments: [] });
  const [saving, setSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/blog');
      setPosts(res.data.posts);
      setTotal(res.data.total);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const resetForm = () => {
    setForm({ title: "", content: "", summary: "", tags: "", status: "draft", attachments: [] });
    setEditorKey(k => k + 1);
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) };
    try {
      if (editId) {
        await apiClient.put(`/admin/blog/${editId}`, payload);
      } else {
        await apiClient.post('/admin/blog', payload);
      }
      resetForm();
      fetchPosts();
    } catch (err) { /* silent */ }
    finally { setSaving(false); }
  };

  const startEdit = (post) => {
    setForm({ title: post.title, content: post.content, summary: post.summary, tags: (post.tags || []).join(", "), status: post.status, attachments: post.attachments || [] });
    setEditorKey(k => k + 1);
    setEditId(post.post_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try { await apiClient.delete(`/admin/blog/${id}`); fetchPosts(); }
    catch (err) { /* silent */ }
  };

  const toggleStatus = async (post) => {
    const newStatus = post.status === "published" ? "draft" : "published";
    try {
      await apiClient.put(`/admin/blog/${post.post_id}`, { status: newStatus });
      fetchPosts();
    } catch (err) { /* silent */ }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-blog">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">Mr. RAPsoul.R Blog</h1>
            <p className="text-white/40 text-sm">Rice And Peas For The Soul REVISITED — {total} posts</p>
          </div>
          <Button onClick={() => { showForm ? resetForm() : setShowForm(true); }} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl gap-2" data-testid="new-blog-btn">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "Cancel" : "New Post"}
          </Button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-[#161821] border border-white/5 rounded-2xl p-6 mb-6 space-y-4" data-testid="blog-form">
            <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Post title..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="blog-title" />
            <Input value={form.summary} onChange={(e) => setForm({...form, summary: e.target.value})} placeholder="Short summary (visible to non-logged-in visitors)..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" required data-testid="blog-summary" />
            <RichTextEditor key={editorKey} content={form.content} onChange={(html) => setForm(prev => ({...prev, content: html}))} placeholder="Write your blog post content..." />
            <Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="Tags (comma-separated)..." className="bg-[#0f1117] border-white/10 text-white placeholder:text-white/30 rounded-xl" data-testid="blog-tags" />
            <div className="flex items-center gap-3">
              <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="bg-[#0f1117] border border-white/10 text-white rounded-xl px-3 py-2" data-testid="blog-status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
              <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl ml-auto" data-testid="blog-submit">
                {saving ? "Saving..." : (editId ? "Update Post" : "Create Post")}
              </Button>
            </div>
            <FileUpload files={form.attachments} onChange={(files) => setForm({...form, attachments: files})} maxFiles={5} />
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
        ) : posts.length > 0 ? (
          <div className="space-y-3">
            {posts.map(p => (
              <div key={p.post_id} className="bg-[#161821] border border-white/5 rounded-2xl p-5" data-testid={`blog-${p.post_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${p.status === "published" ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-white/40"}`}>{p.status}</span>
                      <span className="text-white/30 text-xs">{new Date(p.created_at).toLocaleDateString()}</span>
                      <span className="text-white/20 text-xs">{p.views || 0} views</span>
                    </div>
                    <h3 className="text-white font-medium mb-1">{p.title}</h3>
                    <p className="text-white/50 text-sm mb-2">{p.summary}</p>
                    {p.tags?.length > 0 && (
                      <div className="flex gap-1">{p.tags.map((t, i) => <span key={`tag-${t}-${i}`} className="px-2 py-0.5 bg-white/5 rounded-full text-xs text-white/30">{t}</span>)}</div>
                    )}
                    <AttachmentList attachments={p.attachments} />
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleStatus(p)} className={`p-2 rounded-xl transition-colors ${p.status === "published" ? "hover:bg-amber-500/10 text-emerald-400" : "hover:bg-emerald-500/10 text-white/30"}`} title={p.status === "published" ? "Unpublish" : "Publish"}>
                      {p.status === "published" ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => startEdit(p)} className="p-2 rounded-xl hover:bg-blue-500/10 text-white/30 hover:text-blue-400"><PenLine className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(p.post_id)} className="p-2 rounded-xl hover:bg-red-500/10 text-white/20 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#161821] border border-white/5 rounded-2xl p-12 text-center">
            <PenLine className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/30">No blog posts yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
